<script setup>
import {ref, toRefs, useSlots} from "vue";
import StartupsModalRow from "@/components/startups/StartupsModalRow.vue";
import AddToTrackers from "@/components/manage/SelectTrackersModal.vue";
import trackersCompaniesService from "@/services/trackers-companies.service";
import {constants} from "@/views/search/searchConstants";
import PulseLoader from "vue-spinner/src/MoonLoader.vue";

const props = defineProps({
    startups: {
        type: Array,
    },
    trackerId: {
        type: Number,
        default: null,
    },
    loading: {
        type: Boolean,
        default: false,
    }
});

const {startups} = toRefs(props);
const emit = defineEmits(['addedToTrackers']);
const slots = useSlots();

const showModal = ref(false);
const selectedStartups = ref([]);
const addToTrackersModal = ref(null);
const addingToTrackers = ref(false);

function selectStartup(id, isSelected) {
    if (isSelected) {
        selectedStartups.value.push(id);
    } else {
        selectedStartups.value = selectedStartups.value.filter((item) => item !== id);
    }
}

function selectAll() {
    selectedStartups.value = startups.value.map(s => s.id);
}

function prepareTrackers() {
    if (props.trackerId) {
        addToTrackers([{id: props.trackerId}]);
    } else {
        addToTrackersModal.value.open();
    }
}

async function addToTrackers(trackers) {
    addingToTrackers.value = true;
    await trackersCompaniesService.addCompanies(trackers.map(item => item.id), selectedStartups.value)

    selectedStartups.value = [];
    emit("addedToTrackers");
    addingToTrackers.value = false;
}
function open() {
    selectedStartups.value = [];
    showModal.value = true;
}

function close() {
    showModal.value = false;
}

defineExpose({
    open,
    close
});
</script>

<template>
    <div>
        <b-modal v-model="showModal" modal-class="modal-similar-startups modal-w-xxl" title="Similar startups">
            <template v-if="slots.header" #modal-header>
                <slot name="header"/>
            </template>

            <PulseLoader :loading="loading" :color="constants.spinnerColor"/>
            <table v-if="startups.length" class="main-table">
                <tr>
                    <th></th>
                    <th>Company</th>
                    <th v-if="'score' in startups[0]">Match Level</th>
                    <th>Categories</th>
                </tr>

                <tbody>
                    <StartupsModalRow
                        v-for="startup in startups"
                        :key="startup.id"
                        :startup="startup"
                        :is-selected="selectedStartups.includes(startup.id)"
                        @setSelected="(selected) => selectStartup(startup.id, selected)"
                    />
                </tbody>
            </table>

            <template #modal-footer>
                <ul class="modal-similar-startups__footer">
                    <li>{{selectedStartups.length}} startups selected</li>

                    <li>
                        <b-button variant="primary" size="md" @click="selectAll" class="button-select-all">
                            Select All
                        </b-button>
                    </li>

                    <li>
                        <b-button variant="primary" size="md" @click="selectedStartups = []" class="button-deselect"
                                  :disabled="!selectedStartups.length">
                            Deselect All
                        </b-button>
                    </li>

                    <li>
                        <b-button
                            variant="primary"
                            size="md"
                            class="button-track"
                            @click="prepareTrackers"
                            :disabled="!selectedStartups.length || addingToTrackers"
                        >
                            <template v-if="addingToTrackers">
                                Adding...
                            </template>
                            <template v-else>
                                Add To Tracker
                            </template>
                        </b-button>
                    </li>
                </ul>
            </template>
        </b-modal>

        <AddToTrackers
            v-if="!trackerId"
            ref="addToTrackersModal"
            modalId="add-similar-startups-to-trackers-popup"
            @trackersSelected="addToTrackers"
        />
    </div>
</template>

<style scoped>
    li button:not(:disabled) {
        cursor: pointer;
    }
</style>
